/**
 * P17 — D51's **Search** input: Google Places Autocomplete, called client-side over REST.
 *
 * **REST, not the Maps JavaScript SDK.** The SDK would be a third-party script in the shell's
 * critical path, a CSP entry, and a second source of truth about which APIs the key may reach;
 * `places.googleapis.com` over `fetch` is two requests with a field mask and nothing resident in
 * the page. It also keeps the key's scope honestly equal to what HUMAN-GUIDE row 9 provisions —
 * **Places + Geocoding, and no Maps JavaScript or Static API** — which is what removed the raster
 * map from D66's pin confirmation (§6, and the deviation logged for it).
 *
 * **Session-token billing (D51).** One token spans a keystroke-run *plus* the Details call that
 * resolves it, and is then discarded — that is what makes the run bill as one autocomplete session
 * rather than per keystroke. `beginSession()` mints one; `resolvePlace()` spends it. A token reused
 * after its Details call bills as a fresh session while looking like thrift, so the picker drops
 * its token on every selection and on every clear.
 *
 * **Nothing here persists.** Suggestions and tokens are memory-only for the life of the picker
 * (`device-cache.md`), so there is no cache to invalidate and no query history on disk.
 */

import {
  MAPS_REGION_CODE,
  httpFailureReason,
  mapsConfigured,
  mapsRequestHeaders,
  roundCoordinate,
  type MapsFailure,
} from './config'
import { mapsFetch } from './nativeFetch'

const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete'
const DETAILS_BASE_URL = 'https://places.googleapis.com/v1/places/'

/** 🔸 Enough to choose from without turning the field into a list screen. */
export const MAX_SUGGESTIONS = 5

/** 🔸 Bias radius, in metres, around a device fix that is already in hand. */
const BIAS_RADIUS_METRES = 20_000

export interface PlaceSuggestion {
  readonly placeId: string
  /** The one-line label to render. Never assumed to be the address that gets stored. */
  readonly text: string
}

export interface ResolvedPlace {
  readonly placeId: string
  readonly formatted: string
  readonly lat: number
  readonly lng: number
}

export type SuggestResult =
  { readonly ok: true; readonly suggestions: readonly PlaceSuggestion[] } | MapsFailure
export type ResolveResult = { readonly ok: true; readonly place: ResolvedPlace } | MapsFailure

/** An opaque autocomplete session token. One per keystroke-run plus its resolving Details call. */
export type PlacesSessionToken = string & { readonly __brand: 'PlacesSessionToken' }

/**
 * Mint a session token.
 *
 * `crypto.randomUUID()` is the CSPRNG on both sides here (`globalThis.crypto`, the same rule
 * `packages/core` follows). A weak token does not leak anything — it is a billing correlation id,
 * not a credential — but a *colliding* one merges two users' sessions at Google's end, so the
 * fallback below is a UUID-shaped draw from `getRandomValues`, never `Math.random()`.
 */
export function beginSession(): PlacesSessionToken {
  const uuid =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : randomUuidFallback()
  return uuid as PlacesSessionToken
}

function randomUuidFallback(): string {
  const bytes = new Uint8Array(16)
  globalThis.crypto.getRandomValues(bytes)
  // RFC 4122 §4.4 — version 4, variant 10xx.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export interface SuggestOptions {
  readonly input: string
  readonly sessionToken: PlacesSessionToken
  readonly languageCode: string
  /** The device fix, when one is already in hand. Never captured *for* this call. */
  readonly bias?: { readonly lat: number; readonly lng: number }
  readonly signal?: AbortSignal
  readonly fetchImpl?: typeof fetch
}

/**
 * Query Autocomplete.
 *
 * `includedRegionCodes` is the pilot's `country:il` restriction (D51). The bias is attached only
 * when the caller *already* holds a device fix — this function never triggers a capture, because a
 * position read for the purpose of improving a search ranking is not the position read the
 * just-in-time prompt asked permission for.
 */
export async function suggestPlaces(options: SuggestOptions): Promise<SuggestResult> {
  if (!mapsConfigured()) return { ok: false, reason: 'unconfigured' }
  const input = options.input.trim()
  if (input.length === 0) return { ok: true, suggestions: [] }

  const body = {
    input,
    sessionToken: options.sessionToken,
    languageCode: options.languageCode,
    includedRegionCodes: [MAPS_REGION_CODE],
    ...(options.bias === undefined
      ? {}
      : {
          locationBias: {
            circle: {
              center: { latitude: options.bias.lat, longitude: options.bias.lng },
              radius: BIAS_RADIUS_METRES,
            },
          },
        }),
  }

  let response: Response
  try {
    response = await (options.fetchImpl ?? mapsFetch())(AUTOCOMPLETE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...mapsRequestHeaders(),
      },
      body: JSON.stringify(body),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    })
  } catch {
    // Includes the abort of a superseded keystroke. The caller discards a stale result either way.
    return { ok: false, reason: 'network' }
  }

  if (!response.ok) return { ok: false, reason: httpFailureReason(response.status) }

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    return { ok: false, reason: 'network' }
  }

  const suggestions = readSuggestions(payload)
  return suggestions.length === 0 ? { ok: false, reason: 'no-results' } : { ok: true, suggestions }
}

function readSuggestions(payload: unknown): readonly PlaceSuggestion[] {
  if (typeof payload !== 'object' || payload === null) return []
  const raw = (payload as { readonly suggestions?: unknown }).suggestions
  if (!Array.isArray(raw)) return []

  const out: PlaceSuggestion[] = []
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue
    const prediction = (entry as { readonly placePrediction?: unknown }).placePrediction
    if (typeof prediction !== 'object' || prediction === null) continue
    const placeId = (prediction as { readonly placeId?: unknown }).placeId
    const text = (prediction as { readonly text?: { readonly text?: unknown } }).text?.text
    if (typeof placeId !== 'string' || placeId.length === 0) continue
    if (typeof text !== 'string' || text.length === 0) continue
    out.push({ placeId, text })
    if (out.length === MAX_SUGGESTIONS) break
  }
  return out
}

export interface ResolveOptions {
  readonly placeId: string
  readonly sessionToken: PlacesSessionToken
  readonly languageCode: string
  readonly signal?: AbortSignal
  readonly fetchImpl?: typeof fetch
}

/**
 * Resolve a chosen suggestion to `formatted` + `placeId` + the place's own coordinates.
 *
 * The field mask asks for three fields and no more. It is not a cost optimisation dressed as a
 * privacy note: Places Details bills by SKU tier, and the tiers above these three return the
 * contact and atmosphere data — reviews, phone numbers, opening hours — that the DPIA describes
 * this system as not collecting. Widening the mask would make that description false.
 */
export async function resolvePlace(options: ResolveOptions): Promise<ResolveResult> {
  if (!mapsConfigured()) return { ok: false, reason: 'unconfigured' }

  const url = new URL(DETAILS_BASE_URL + encodeURIComponent(options.placeId))
  url.searchParams.set('languageCode', options.languageCode)
  url.searchParams.set('regionCode', MAPS_REGION_CODE)
  url.searchParams.set('sessionToken', options.sessionToken)

  let response: Response
  try {
    response = await (options.fetchImpl ?? mapsFetch())(url.toString(), {
      method: 'GET',
      headers: {
        ...mapsRequestHeaders(),
        'X-Goog-FieldMask': 'id,formattedAddress,location',
      },
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    })
  } catch {
    return { ok: false, reason: 'network' }
  }

  if (!response.ok) return { ok: false, reason: httpFailureReason(response.status) }

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    return { ok: false, reason: 'network' }
  }

  const place = readPlace(payload, options.placeId)
  return place === undefined ? { ok: false, reason: 'no-results' } : { ok: true, place }
}

function readPlace(payload: unknown, requestedPlaceId: string): ResolvedPlace | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined
  const value = payload as {
    readonly id?: unknown
    readonly formattedAddress?: unknown
    readonly location?: { readonly latitude?: unknown; readonly longitude?: unknown }
  }
  const formatted = value.formattedAddress
  const lat = value.location?.latitude
  const lng = value.location?.longitude
  if (typeof formatted !== 'string' || formatted.length === 0) return undefined
  if (typeof lat !== 'number' || typeof lng !== 'number') return undefined
  // A `places` row without coordinates is not storable — P3 rejects it — so a partial answer is a
  // failure here rather than a silent downgrade to `manual`, which would mislabel where the
  // address came from. Provenance is the whole point of `source`.
  const placeId = typeof value.id === 'string' && value.id.length > 0 ? value.id : requestedPlaceId
  return { placeId, formatted, lat: roundCoordinate(lat), lng: roundCoordinate(lng) }
}
